import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchDashboardCards, markFollowUpDone } from "../../../services/dashboardService";
import CreateClientModal from "../../ClientManagement/client/CreateClientModal";
import CreateJobModal from "../../ATS/CreateJobModal";
import CandidateCreateModel from "../../Candidates/CreateCandidateModal";

// ─── Theme ────────────────────────────────────────────────────────────────────
const NAVY  = "#1e3a8a";

// ─── 16 card type definitions ─────────────────────────────────────────────────
const CARD_TYPES = [
  { type: "INVOICE_OVERDUE",             label: "Invoice Overdue",          bucket: "MONEY" },
  { type: "HIRED_NOT_INVOICED",          label: "Hired Not Invoiced",       bucket: "MONEY" },
  { type: "DRAFT_INVOICE_SITTING",       label: "Draft Invoice Unsent",     bucket: "MONEY" },
  { type: "PAYMENT_DUE_THIS_WEEK",       label: "Payment Due This Week",    bucket: "MONEY" },
  { type: "CLIENT_GONE_COLD",            label: "Client Gone Cold",         bucket: "RELATIONSHIPS" },
  { type: "FOLLOW_UP_DUE_TODAY",         label: "Follow-up Due Today",      bucket: "RELATIONSHIPS" },
  { type: "FOLLOW_UP_OVERDUE",           label: "Follow-up Overdue",        bucket: "RELATIONSHIPS" },
  { type: "PROPOSAL_NO_RESPONSE",        label: "Proposal No Response",     bucket: "RELATIONSHIPS" },
  { type: "CANDIDATES_WAITING_FEEDBACK", label: "Awaiting Client Feedback", bucket: "PIPELINE" },
  { type: "CLIENT_VIEWED_NO_DECISION",   label: "Viewed — No Decision",     bucket: "PIPELINE" },
  { type: "INTERVIEW_TO_SCHEDULE",       label: "Interview to Schedule",    bucket: "PIPELINE" },
  { type: "INTERVIEW_TO_CONFIRM",        label: "Interview to Confirm",     bucket: "PIPELINE" },
  { type: "CANDIDATE_GONE_SILENT",       label: "Candidate Gone Silent",    bucket: "PIPELINE" },
  { type: "OFFER_NO_RESPONSE",           label: "Offer No Response",        bucket: "PIPELINE" },
  { type: "JOINING_DATE_PASSED",         label: "Joining Date Passed",      bucket: "PIPELINE" },
  { type: "JOB_MANDATE_IDLE",            label: "Job Mandate Idle",         bucket: "PIPELINE" },
];

const BUCKETS = [
  { key: "MONEY",         label: "Money at Risk",         icon: "💰" },
  { key: "RELATIONSHIPS", label: "Relationships at Risk", icon: "🤝" },
  { key: "PIPELINE",      label: "Pipeline at Risk",      icon: "📋" },
];

const SEVERITY_ORDER = { RED: 0, ORANGE: 1, YELLOW: 2 };
const SEV = {
  RED:    { bg: "#fff5f5", border: "#fc8181", dot: "#e53e3e", btn: "#e53e3e" },
  ORANGE: { bg: "#fffaf0", border: "#f6ad55", dot: "#dd6b20", btn: "#dd6b20" },
  YELLOW: { bg: "#fffff0", border: "#f6e05e", dot: "#d69e2e", btn: "#d69e2e" },
};

// ─── Onboarding steps ─────────────────────────────────────────────────────────
const STEPS = [
  { num: 1, label: "+ Add Client",    sub: "Who you're placing for",   route: "/clients" },
  { num: 2, label: "+ Post Job",      sub: "What role you're filling", route: "/ats/jobs" },
  { num: 3, label: "+ Add Candidate", sub: "Who you're placing",       route: "/ats/all-candidates" },
  { num: 4, label: "+ Create Invoice",sub: "Get paid for the work",    route: "/invoice" },
];

// ─── Badge ────────────────────────────────────────────────────────────────────
function Badge({ label, bg, color, border }) {
  return (
    <span style={{
      display: "inline-block", background: bg, color,
      border: `1px solid ${border}`, borderRadius: 4,
      fontSize: 10, fontWeight: 700, padding: "2px 7px",
      letterSpacing: "0.04em", whiteSpace: "nowrap",
    }}>{label}</span>
  );
}

// ─── Card type → route mapping ────────────────────────────────────────────────
// Each card knows its entityType and entityId; we map cardType to the module
// route the user should land on to take action.
const CARD_ROUTES = {
  INVOICE_OVERDUE:             "/invoice",
  HIRED_NOT_INVOICED:          "/invoice",
  DRAFT_INVOICE_SITTING:       "/invoice",
  PAYMENT_DUE_THIS_WEEK:       "/invoice",
  CLIENT_GONE_COLD:            "/clients",
  FOLLOW_UP_DUE_TODAY:         "/tasks",
  FOLLOW_UP_OVERDUE:           "/tasks",
  PROPOSAL_NO_RESPONSE:        "/clients",
  CANDIDATES_WAITING_FEEDBACK: "/ats/candidatesharing",
  CLIENT_VIEWED_NO_DECISION:   "/ats/candidatesharing",
  INTERVIEW_TO_SCHEDULE:       "/ats/all-candidates",
  INTERVIEW_TO_CONFIRM:        "/ats/all-candidates",
  CANDIDATE_GONE_SILENT:       "/ats/all-candidates",
  OFFER_NO_RESPONSE:           "/ats/all-candidates",
  JOINING_DATE_PASSED:         "/ats/all-candidates",
  JOB_MANDATE_IDLE:            "/ats/jobs",
};

const SAMPLE_CARD_ROUTES = {
  INVOICE_OVERDUE:  "/invoice",
  CLIENT_GONE_COLD: "/clients",
  JOB_MANDATE_IDLE: "/ats/all-candidates",
};

// ─── Action Card ─────────────────────────────────────────────────────────────
function ActionCard({ card, onDone, onNavigate }) {
  const sev = SEV[card.severity] || SEV.YELLOW;
  return (
    <div style={{
      background: "#fff", border: `1px solid ${sev.border}`,
      borderLeft: `3px solid ${sev.dot}`, borderRadius: 8,
      padding: "14px 16px 16px", display: "flex", flexDirection: "column", gap: 10,
      position: "relative",
    }}>
      {card.isSample && (
        <span className="absolute top-2 right-2 text-[10px] font-medium bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full uppercase tracking-wide">Example</span>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#1a202c", lineHeight: 1.45 }}>{card.title}</span>
        <span onClick={() => onDone?.(card)} title="Dismiss"
          style={{ fontSize: 15, color: "#cbd5e0", cursor: "pointer", flexShrink: 0, marginTop: 1 }}>✕</span>
      </div>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
        {(card.badges || []).map((b, i) => <Badge key={i} {...b} />)}
      </div>
      <div style={{ fontSize: 12, color: "#718096", lineHeight: 1.4 }}>{card.meta}</div>
      <button onClick={() => onNavigate?.(card)} style={{
        background: sev.btn, color: "#fff", border: "none", borderRadius: 5,
        padding: "6px 13px", fontSize: 12, fontWeight: 600, cursor: "pointer", alignSelf: "flex-start",
      }}>{card.action}</button>
    </div>
  );
}

// ─── Card Type Row (always visible when dashboard active) ──────────────────
function CardTypeRow({ cardType, cards, onDone, onNavigate }) {
  const [expanded, setExpanded] = useState(true);
  const count = cards.length;
  const topSev = count > 0
    ? cards.reduce((b, c) => (SEVERITY_ORDER[c.severity] ?? 3) < (SEVERITY_ORDER[b] ?? 3) ? c.severity : b, cards[0].severity)
    : null;
  const sev = topSev ? SEV[topSev] : null;

  return (
    <div style={{
      background: "#fff",
      border: `1px solid ${count > 0 ? (sev?.border || "#e2e8f0") : "#e9eef6"}`,
      borderRadius: 8, overflow: "hidden", marginBottom: 8,
    }}>
      <div onClick={() => count > 0 && setExpanded(e => !e)} style={{
        padding: "10px 16px", display: "flex", alignItems: "center",
        justifyContent: "space-between",
        cursor: count > 0 ? "pointer" : "default",
        background: count > 0 ? (sev?.bg || "#fff") : "#fafbfd",
        userSelect: "none",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {count > 0 && (
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: sev?.dot, display: "inline-block" }} />
          )}
          <span style={{ fontSize: 13, fontWeight: 600, color: count > 0 ? "#1a202c" : "#94a3b8" }}>
            {cardType.label}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {count > 0 ? (
            <>
              <span style={{
                background: sev?.dot, color: "#fff", fontSize: 11, fontWeight: 700,
                padding: "2px 9px", borderRadius: 10, minWidth: 22, textAlign: "center",
              }}>{count}</span>
              <span style={{ color: "#94a3b8", fontSize: 11 }}>{expanded ? "▲" : "▼"}</span>
            </>
          ) : (
            <span style={{ fontSize: 12, color: "#22c55e", fontWeight: 600 }}>✓ All good</span>
          )}
        </div>
      </div>
      {expanded && count > 0 && (
        <div style={{
          padding: "12px 16px", borderTop: `1px solid ${sev?.border || "#e2e8f0"}`,
          display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10,
        }}>
          {cards.map((c, i) => <ActionCard key={c.entityId || i} card={c} onDone={onDone} onNavigate={onNavigate} />)}
        </div>
      )}
    </div>
  );
}

// ─── Bucket Section ───────────────────────────────────────────────────────────
function BucketSection({ bucket, cardsByType, onDone, onNavigate }) {
  const types = CARD_TYPES.filter(ct => ct.bucket === bucket.key);
  const total = types.reduce((s, ct) => s + (cardsByType[ct.type]?.length || 0), 0);
  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>{bucket.icon}</span>
          <span style={{
            fontSize: 12, fontWeight: 700, color: "#334155",
            textTransform: "uppercase", letterSpacing: "0.07em",
          }}>{bucket.label}</span>
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: total > 0 ? "#e53e3e" : "#94a3b8" }}>
          {total > 0 ? `${total} action${total > 1 ? "s" : ""}` : "All clear"}
        </span>
      </div>
      {types.filter(ct => (cardsByType[ct.type]?.length || 0) > 0).map(ct => (
        <CardTypeRow key={ct.type} cardType={ct} cards={cardsByType[ct.type] || []} onDone={onDone} onNavigate={onNavigate} />
      ))}
    </div>
  );
}

// ─── Onboarding Flow — compact collapsible strip ─────────────────────────────
function OnboardingFlow({ data, onStepAction }) {
  const [expanded, setExpanded] = useState(false);
  const { clientCount = 0, jobCount = 0, candidateCount = 0, hasJoinedWithoutInvoice = false } = data || {};

  let activeStep;
  if (clientCount === 0)            activeStep = 1;
  else if (jobCount === 0)          activeStep = 2;
  else if (candidateCount === 0)    activeStep = 3;
  else if (hasJoinedWithoutInvoice) activeStep = 4;
  else                              activeStep = 5;

  if (activeStep === 5) return null;

  const completedSteps = activeStep - 1;
  const progress = (completedSteps / 4) * 100;
  const currentStep = STEPS[activeStep - 1];
  const accentColor = activeStep === 4 ? "#dc2626" : NAVY;

  return (
    <div style={{
      background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8,
      overflow: "hidden", marginBottom: 16,
    }}>
      {/* Thin header strip */}
      <div style={{
        padding: "9px 14px",
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <span style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap", flexShrink: 0 }}>
          Setup
        </span>
        <div style={{ flex: 1, background: "#e2e8f0", borderRadius: 99, height: 5, overflow: "hidden", minWidth: 60 }}>
          <div style={{
            width: `${progress}%`,
            background: accentColor,
            height: "100%", borderRadius: 99,
            transition: "width 0.4s ease",
          }} />
        </div>
        <span style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap", flexShrink: 0 }}>
          {completedSteps}/4
        </span>
        <button
          onClick={() => onStepAction(activeStep)}
          style={{
            background: accentColor, color: "#fff",
            border: "none", borderRadius: 5,
            padding: "4px 11px", fontSize: 11, fontWeight: 600,
            cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
          }}
        >
          {currentStep.label}
        </button>
        <button
          onClick={() => setExpanded(e => !e)}
          style={{
            background: "none", border: "none",
            color: "#cbd5e1", fontSize: 10,
            cursor: "pointer", padding: "2px 2px", flexShrink: 0,
          }}
          title={expanded ? "Collapse" : "Show steps"}
        >
          {expanded ? "▲" : "▼"}
        </button>
      </div>

      {/* Expanded step list */}
      {expanded && (
        <div style={{
          borderTop: "1px solid #f1f5f9",
          padding: "12px 14px",
          display: "flex", gap: 8,
        }}>
          {STEPS.map(step => {
            const isCompleted = step.num < activeStep;
            const isActive    = step.num === activeStep;
            const isDisabled  = step.num > activeStep;
            return (
              <div key={step.num} style={{
                flex: "1 1 0",
                border: `1px solid ${isActive ? accentColor : isCompleted ? "#86efac" : "#e2e8f0"}`,
                borderRadius: 6, padding: "9px 10px",
                background: isCompleted ? "#f0fdf4" : isDisabled ? "#fafbfd" : "#fff",
                opacity: isDisabled ? 0.5 : 1,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                  <span style={{
                    width: 18, height: 18, borderRadius: "50%",
                    background: isCompleted ? "#22c55e" : isActive ? accentColor : "#e2e8f0",
                    color: "#fff",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, fontWeight: 700, flexShrink: 0,
                  }}>
                    {isCompleted ? "✓" : step.num}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: isDisabled ? "#94a3b8" : "#1e293b" }}>
                    {step.label}
                  </span>
                </div>
                <div style={{ fontSize: 10, color: "#94a3b8", paddingLeft: 24 }}>{step.sub}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ActionDashboard() {
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [dismissed, setDismissed] = useState(new Set());
  const [doneItems, setDoneItems] = useState([]);
  const [doneOpen, setDoneOpen]   = useState(false);
  const [openModal, setOpenModal] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      setData(await fetchDashboardCards());
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load dashboard.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleStepAction = useCallback((stepNum) => {
    if (stepNum === 1) setOpenModal('client');
    else if (stepNum === 2) setOpenModal('job');
    else if (stepNum === 3) setOpenModal('candidate');
    else navigate('/invoice', { state: { openCreate: true } });
  }, [navigate]);

  const handleDone = useCallback(async (card) => {
    setDismissed(prev => new Set([...prev, card.entityId]));
    setDoneItems(prev => [
      { text: card.title, time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) },
      ...prev,
    ]);
    if (card.entityType === "follow_up") {
      try { await markFollowUpDone(card.entityId); } catch { /* silent */ }
    }
  }, []);

  const handleNavigate = useCallback((card) => {
    if (card.isSample) {
      const sampleRoute = SAMPLE_CARD_ROUTES[card.cardType];
      if (sampleRoute) navigate(sampleRoute, { state: { openCreate: true } });
      return;
    }
    const route = CARD_ROUTES[card.cardType];
    if (route) navigate(route);
    handleDone(card);
  }, [navigate, handleDone]);

  // Derived state
  const allCards = data
    ? [...(data.moneyAtRisk || []), ...(data.relationshipsAtRisk || []), ...(data.pipelineAtRisk || [])]
    : [];
  const visible = allCards.filter(c => !dismissed.has(c.entityId));

  const cardsByType = {};
  CARD_TYPES.forEach(ct => { cardsByType[ct.type] = []; });
  visible.forEach(c => {
    if (cardsByType[c.cardType]) cardsByType[c.cardType].push(c);
    else cardsByType[c.cardType] = [c];
  });

  const urgentCount   = visible.filter(c => c.severity === "RED").length;
  const warningCount  = visible.filter(c => c.severity === "ORANGE").length;
  const reminderCount = visible.filter(c => c.severity === "YELLOW").length;

  // ── Loading ──────────────────────────────────────────────────
  if (loading) return (
    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc" }}>
      <div style={{ textAlign: "center", color: "#94a3b8" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
        <div style={{ fontSize: 14, fontWeight: 500 }}>Loading your dashboard…</div>
      </div>
    </div>
  );

  // ── Error ────────────────────────────────────────────────────
  if (error) return (
    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc" }}>
      <div style={{ textAlign: "center", color: "#dc2626", maxWidth: 360 }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontSize: 14, marginBottom: 16 }}>{error}</div>
        <button onClick={loadData} style={{
          background: "#dc2626", color: "#fff", border: "none",
          borderRadius: 6, padding: "8px 20px", cursor: "pointer", fontWeight: 600,
        }}>Retry</button>
      </div>
    </div>
  );

  return (
    <div style={{
      height: "100%", overflowY: "auto", background: "#f8fafc",
      fontFamily: "'Inter', 'Segoe UI', sans-serif", display: "flex", flexDirection: "column",
    }}>

      {/* ── Header ── */}
      <div style={{
        background: "#fff", borderBottom: "1px solid #e2e8f0",
        padding: "14px 28px", display: "flex",
        alignItems: "center", justifyContent: "space-between", flexShrink: 0,
      }}>
        <div>
          <span style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>
            {data?.greeting || "Good morning"}
          </span>
          <span style={{ fontSize: 14, color: "#94a3b8", fontWeight: 400 }}>
            {" "}— {data?.date || new Date().toDateString()}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {doneItems.length > 0 && (
            <button onClick={() => setDoneOpen(o => !o)} style={{
              background: "#f0fdf4", color: "#16a34a", border: "1px solid #86efac",
              fontSize: 12, fontWeight: 600, padding: "5px 14px", borderRadius: 20,
              cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
            }}>
              ✓ {doneItems.length} Done today
            </button>
          )}
          <button onClick={loadData} title="Refresh" style={{
            background: "none", border: "1px solid #e2e8f0", borderRadius: 7,
            padding: "5px 10px", cursor: "pointer", fontSize: 14, color: "#94a3b8",
          }}>🔄</button>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, overflowY: "auto" }}>

        {/* ── LIVE DASHBOARD — always rendered; sample cards handle empty state ── */}
        <div style={{ padding: "24px 28px" }}>

          {/* Summary stat row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 24 }}>
            {[
              { icon: "⚠️", color: "#dc2626", border: "#fca5a5", bg: "#fff5f5", count: urgentCount,   label: "Urgent — needs action now" },
              { icon: "⚡", color: "#d97706", border: "#fcd34d", bg: "#fffbeb", count: warningCount,  label: "Warning — going stale soon" },
              { icon: "🔔", color: "#ca8a04", border: "#fde68a", bg: "#fefce8", count: reminderCount, label: "Reminder — due today" },
            ].map((s, i) => (
              <div key={i} style={{
                background: s.bg, border: `1px solid ${s.border}`,
                borderRadius: 10, padding: "16px 20px",
                display: "flex", alignItems: "center", gap: 14,
              }}>
                <span style={{ fontSize: 24 }}>{s.icon}</span>
                <div>
                  <div style={{ fontSize: 30, fontWeight: 800, color: "#1e293b", lineHeight: 1 }}>{s.count}</div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Action banner — only shown when real action items exist */}
          {urgentCount + warningCount > 0 && (
            <div style={{
              background: "#fff5f5", border: "1px solid #fca5a5", borderRadius: 8,
              padding: "10px 16px", marginBottom: 24,
              display: "flex", alignItems: "center", gap: 10,
              fontSize: 13, color: "#dc2626", fontWeight: 600,
            }}>
              <span>⚡</span>
              {urgentCount + warningCount} action item{urgentCount + warningCount > 1 ? "s" : ""} need your attention across the business.
            </div>
          )}

          {/* 16 card panels in 3 buckets */}
          {BUCKETS.map(bucket => (
            <BucketSection key={bucket.key} bucket={bucket} cardsByType={cardsByType} onDone={handleDone} onNavigate={handleNavigate} />
          ))}

          {/* Done Today drawer */}
          {doneItems.length > 0 && (
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", marginBottom: 24 }}>
              <div onClick={() => setDoneOpen(o => !o)} style={{
                padding: "13px 16px", display: "flex", alignItems: "center",
                justifyContent: "space-between", cursor: "pointer", userSelect: "none",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: "#16a34a" }}>
                  <span>✓</span><span>Done Today</span>
                  <span style={{ background: "#dcfce7", color: "#16a34a", fontSize: 11, fontWeight: 700, padding: "1px 8px", borderRadius: 10 }}>
                    {doneItems.length}
                  </span>
                </div>
                <span style={{ color: "#94a3b8", fontSize: 12 }}>{doneOpen ? "▲" : "▼"}</span>
              </div>
              {doneOpen && (
                <div style={{ borderTop: "1px solid #f1f5f9" }}>
                  {doneItems.map((d, i) => (
                    <div key={i} style={{
                      padding: "10px 16px",
                      borderBottom: i < doneItems.length - 1 ? "1px solid #f8fafc" : "none",
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#475569" }}>
                        <span style={{ color: "#22c55e", fontWeight: 700 }}>✓</span>
                        {d.text}
                      </div>
                      <span style={{ fontSize: 12, color: "#94a3b8" }}>{d.time}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Setup progress bar — visible until all 4 onboarding steps are done */}
          <OnboardingFlow data={data} onStepAction={handleStepAction} />
        </div>
      </div>

      {openModal === 'client' && (
        <CreateClientModal
          onClose={() => setOpenModal(null)}
          onCreate={() => { setOpenModal(null); loadData(); }}
        />
      )}
      {openModal === 'job' && (
        <CreateJobModal
          onClose={() => setOpenModal(null)}
          onCreate={() => { setOpenModal(null); loadData(); }}
        />
      )}
      {openModal === 'candidate' && (
        <CandidateCreateModel
          onClose={() => setOpenModal(null)}
          onSave={() => { setOpenModal(null); loadData(); }}
        />
      )}
    </div>
  );
}
